import { catchError, map, of, switchMap } from 'rxjs';

import { inject, Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { RemitterService } from './remitter.service';

@Injectable({
  providedIn: 'root'
})
export class ValidatorsService {
  public readonly REGEX = {
    PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d!$%@#£€*?&]{8,}$/,
    DATE: /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
  };

  public readonly errorMessages: Record<string, string> = {
    required: 'errors.required',
    email: 'errors.email',
    invalidPostalCode: 'errors.invalidPostalCode',
    invalidMonth: 'errors.invalidMonth',
    invalidDate: 'errors.invalidDate',
    invalidYear: 'errors.invalidYear',
    pattern: 'errors.pattern',
    matching: 'errors.matching',
    invalidIdNo: 'errors.id_no',
    existedUsername: 'errors.existed_username',
    existedEmail: 'errors.existed_email',
    existedIdNo: 'errors.existed_id_no',
    existedMobile: 'errors.existed_mobile'
  };

  public comparePassword(matchTo: string, reverse?: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.parent && reverse) {
        const c = (control.parent?.controls as any)[matchTo] as AbstractControl;
        if (c) {
          c.updateValueAndValidity();
        }
        return null;
      }
      const trimmedValue = control.value ? control.value.trim() : '';

      return !!control.parent &&
        !!control.parent.value &&
        trimmedValue === ((control.parent?.controls as any)[matchTo]?.value || '').trim()
        ? null
        : { matching: true };
    };
  }

  private isLeapYear(year: number) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  }

  public dateValidator(month: number, year: number) {
    let daysOfMonth: number;
    if (month === 2) {
      daysOfMonth = this.isLeapYear(year) ? 29 : 28;
    } else daysOfMonth = [1, 3, 5, 7, 8, 10, 12].includes(month) ? 31 : 30;
    return (control: AbstractControl) => {
      const value = Number.parseInt(control.value);
      if (!control.value) return null;
      return isNaN(value) || value < 1 || value > daysOfMonth ? { invalidDate: true } : null;
    };
  }

  public monthValidator(control: AbstractControl) {
    const value = Number.parseInt(control.value);
    if (!control.value) return null;
    return isNaN(value) || value < 1 || value > 12 ? { invalidMonth: true } : null;
  }

  public yearValidator(control: AbstractControl) {
    const value = Number.parseInt(control.value);
    if (!control.value) return null;
    return isNaN(value) || value < 1900 ? { invalidYear: true } : null;
  }

  public readonly expiryDateValidator = (control: AbstractControl) => {
    const expiryDate = new Date(control.value);
    const today = new Date();
    return expiryDate.getTime() - today.getTime() > 0 ? null : { invalidDate: true };
  };

  private readonly remitterService = inject(RemitterService);

  public readonly validIdNoValidator =
    (idTypeName: string, idCountryName: string) => (control: AbstractControl) => {
      const parent = control.parent;
      const idType = parent?.get(idTypeName);
      const idCountry = parent?.get(idCountryName);
      if (!idType || !idCountry) return of(null);
      const validIdNo$ = this.remitterService
        .validateIdNo(idCountry.value, idType.value, control.value)
        .pipe(map(({ valid }) => (valid ? null : { invalidIdNo: true })));
      return this.uniqueValidator('id_no')(control).pipe(
        switchMap(invalid => (invalid ? of(invalid) : validIdNo$))
      );
    };

  public readonly uniqueValidator =
    (type: 'id_no' | 'email' | 'username') => (control: AbstractControl) => {
      const value = control.value;
      const errorKey = (() => {
        switch (type) {
          case 'email':
            return 'existedEmail';
          case 'id_no':
            return 'existedIdNo';
          case 'username':
            return 'existedUsername';
        }
      })();
      return this.remitterService.validateUnique(type, value).pipe(
        map(() => null),
        catchError(() =>
          of({
            [errorKey]: true
          })
        )
      );
    };
}
